import React, { useState } from "react";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { AlertCircle } from "lucide-react";
import ImageUploader from "./ImageUploader";
import { fileIdFromRef } from "../lib/fileUrl";

const CATEGORIES = [
  { id: "damage", label: "Damage" },
  { id: "cleanliness", label: "Cleanliness" },
  { id: "payment_refund", label: "Payment / refund" },
  { id: "no_show", label: "No-show" },
  { id: "fraud", label: "Fraud" },
  { id: "other", label: "Other" },
];

export default function ReportIssueModal({ booking, open, onClose, onSubmitted }) {
  const [category, setCategory] = useState("other");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachmentUrls, setAttachmentUrls] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setCategory("other");
    setSubject("");
    setBody("");
    setAttachmentUrls([]);
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const submit = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Please add a subject and description");
      return;
    }
    const attachment_file_ids = attachmentUrls
      .map((url) => fileIdFromRef(url))
      .filter(Boolean);
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/support/cases`,
        {
          booking_id: booking?.booking_id || undefined,
          category,
          subject: subject.trim(),
          body: body.trim(),
          ...(attachment_file_ids.length ? { attachment_file_ids } : {}),
        },
        { withCredentials: true },
      );
      // KAN-222: surface the Case # (also emailed to the reporter)
      toast.success(
        data?.case_id ? `Issue reported — Case # ${data.case_id}` : "Issue reported — our team will follow up",
        {
          description: data?.case_id
            ? "We've emailed you a confirmation. Track replies under Profile → Support cases."
            : undefined,
          duration: 8000,
        },
      );
      reset();
      onSubmitted?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit report");
    } finally {
      setSubmitting(false);
    }
  };

  const vehicleLabel = booking?.vehicle_snapshot
    ? `${booking.vehicle_snapshot.year || ""} ${booking.vehicle_snapshot.make || ""} ${booking.vehicle_snapshot.model || ""}`.trim()
    : null;
  const spotLabel = booking?.spot_snapshot?.name || null;
  const listingLabel = vehicleLabel || spotLabel;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="bg-white border border-[var(--zc-border)] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="report-issue-modal">
        <DialogHeader>
          <DialogTitle className="zc-headline text-2xl flex items-center gap-2">
            <AlertCircle size={20} className="text-[var(--zc-accent)]" />
            Report an issue
          </DialogTitle>
        </DialogHeader>
        {booking?.booking_id && (
          <div className="text-sm text-[var(--zc-text-2)]">
            Booking <span className="font-mono text-xs">{booking.booking_id}</span>
            {listingLabel && <> · {listingLabel}</>}
          </div>
        )}
        <div className="space-y-4 mt-2">
          <div>
            <label className="text-xs uppercase tracking-widest text-[var(--zc-text-3)] font-semibold">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="zc-input w-full h-10 px-3 text-sm mt-1"
              data-testid="report-issue-category"
            >
              {CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[var(--zc-text-3)] font-semibold">Subject</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="zc-input w-full h-10 px-3 text-sm mt-1"
              placeholder="Brief summary"
              maxLength={200}
              data-testid="report-issue-subject"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[var(--zc-text-3)] font-semibold">Description</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="zc-input w-full p-3 text-sm mt-1 h-28"
              placeholder="What happened? Include dates, amounts, and attach photos if helpful."
              data-testid="report-issue-body"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[var(--zc-text-3)] font-semibold">
              Photos (optional)
            </label>
            <div className="mt-1">
              <ImageUploader
                value={attachmentUrls}
                onChange={setAttachmentUrls}
                max={5}
                testid="report-issue-attachments"
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <button type="button" onClick={handleClose} className="px-4 h-10 zc-btn-ghost text-sm" disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="px-4 h-10 zc-btn-primary text-sm disabled:opacity-50"
            data-testid="report-issue-submit"
          >
            {submitting ? "Submitting…" : "Submit report"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
